// Billing API Service (mock + future real integration)
// Provides lookup, current bill retrieval, and payment submission.
// Switches between mock and real modes based on VITE_BILLING_API_USE_MOCK.

import { BILLING_ROUTES, getBillingApiBaseUrl } from '../utils/apiRoutes';
import { DEBUG_CONFIG } from '../config';

const USE_MOCK = (import.meta.env.VITE_BILLING_API_USE_MOCK || 'false') === 'true';
const BILLING_API_UNAVAILABLE_MESSAGE = 'Billing service is temporarily unavailable. Please try again in a few minutes.';

const logBillingDebug = (message, detail) => {
  if (!DEBUG_CONFIG.LOG_API_CALLS) return;

  if (detail === undefined) {
    console.log(`[billingApi] ${message}`);
    return;
  }

  console.log(`[billingApi] ${message}`, detail);
};

// ----- Mock Data -----
const mockAccount = {
  accountNumber: '4578-9012-3456',
  phoneNumber: '(555) 123-4567',
  customerName: 'Jane Smith',
  serviceAddress: {
    street: '1234 Oak Street',
    city: 'Charlotte',
    state: 'NC',
    postalCode: '28205'
  }
};

const toIsoDate = (d) => {
  const copy = new Date(d);
  // Normalize to local noon to reduce DST edge cases when converting to ISO.
  copy.setHours(12, 0, 0, 0);
  return copy.toISOString().slice(0, 10);
};

const addDays = (d, days) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const addMonths = (d, months) => {
  const copy = new Date(d);
  copy.setMonth(copy.getMonth() + months);
  return copy;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const diffInDays = (start, end) => Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

const formatBillMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const roundCurrency = (value) => Number(value.toFixed(2));

const MONTHLY_AVG_TEMP_F = [43, 47, 55, 63, 71, 78, 82, 80, 74, 64, 55, 46];
const MONTHLY_USAGE_BIAS = [260, 220, 170, 90, 35, 70, 150, 175, 120, 80, 165, 245];

const getMonthlyUsageKwh = (billMonthStart) => {
  const monthIndex = billMonthStart.getMonth();
  const year = billMonthStart.getFullYear();
  const variance = ((year * 17 + (monthIndex + 1) * 29) % 70) - 35;
  return 900 + MONTHLY_USAGE_BIAS[monthIndex] + variance;
};

const getEnergyRate = (billMonthStart) => {
  const rateOffset = (((billMonthStart.getFullYear() + billMonthStart.getMonth()) % 5) - 2) * 0.0012;
  return Number((0.1135 + rateOffset).toFixed(4));
};

const buildMonthlyBillData = (billMonthStart) => {
  const periodStart = startOfMonth(billMonthStart);
  const periodEnd = endOfMonth(billMonthStart);
  const periodDays = diffInDays(periodStart, periodEnd);
  const dueDate = addDays(periodEnd, 30);
  const usageKwh = getMonthlyUsageKwh(periodStart);
  const previousUsageKwh = getMonthlyUsageKwh(addMonths(periodStart, -1));
  const basicCharge = 12.50;
  const energyAmount = roundCurrency(usageKwh * getEnergyRate(periodStart));
  const taxAmount = roundCurrency((basicCharge + energyAmount) * 0.1205);
  const amountDue = roundCurrency(basicCharge + energyAmount + taxAmount);
  const kwhChangePct = previousUsageKwh > 0
    ? Math.round(((usageKwh - previousUsageKwh) / previousUsageKwh) * 100)
    : 0;

  return {
    billMonth: formatBillMonth(periodStart),
    periodStart,
    periodEnd,
    periodDays,
    dueDate,
    usageKwh,
    avgTempF: MONTHLY_AVG_TEMP_F[periodStart.getMonth()],
    basicCharge,
    energyAmount,
    taxAmount,
    amountDue,
    dailyAvgCost: roundCurrency(amountDue / periodDays),
    kwhChangePct
  };
};

const createMockBill = (billMonthStart) => {
  const current = buildMonthlyBillData(billMonthStart);
  const previous = buildMonthlyBillData(addMonths(billMonthStart, -1));

  return {
    accountNumber: mockAccount.accountNumber,
    billMonth: current.billMonth,
    period: {
      start: toIsoDate(current.periodStart),
      end: toIsoDate(current.periodEnd),
      days: current.periodDays,
      dueDate: toIsoDate(current.dueDate)
    },
    lineItems: [
      { code: 'PREV_BAL', description: 'Previous Balance', amount: previous.amountDue },
      { code: 'PAYMENT_RCVD', description: 'Payment Received - Thank You!', amount: -previous.amountDue },
      { code: 'BASIC_CHG', description: 'Basic Service Charge', amount: current.basicCharge },
      {
        code: 'ENERGY',
        description: `Energy Charge (${current.usageKwh.toLocaleString()} kWh × $${getEnergyRate(current.periodStart).toFixed(4)}/kWh)`,
        amount: current.energyAmount
      },
      { code: 'TAX', description: 'State/Local Taxes', amount: current.taxAmount }
    ],
    usage: {
      kwh: current.usageKwh,
      avgTempF: current.avgTempF,
      dailyAvgCost: current.dailyAvgCost,
      kwhChangePct: current.kwhChangePct
    },
    totals: { amountDue: current.amountDue },
    status: 'OPEN'
  };
};

const createMockHistoryRow = (billMonthStart) => {
  const bill = buildMonthlyBillData(billMonthStart);
  const paidAt = addDays(bill.dueDate, -5);
  const yy = String(paidAt.getFullYear()).slice(-2);
  const dd = String(paidAt.getDate()).padStart(2, '0');
  const mm = String(paidAt.getMonth() + 1).padStart(2, '0');

  return {
    billMonth: bill.billMonth,
    confirmationCode: `CNF${yy}${dd}${mm}`,
    period: {
      start: toIsoDate(bill.periodStart),
      end: toIsoDate(bill.periodEnd),
      days: bill.periodDays,
      dueDate: toIsoDate(bill.dueDate)
    },
    amountDue: bill.amountDue,
    amountPaid: bill.amountDue,
    status: 'PAID',
    paidAt: toIsoDate(paidAt)
  };
};

const createMockBillingDataset = (today = new Date(), historyMonths = 11) => {
  const currentBillMonthStart = startOfMonth(addMonths(today, -1));
  const currentBill = createMockBill(currentBillMonthStart);
  const history = [];

  for (let monthOffset = 1; monthOffset <= historyMonths; monthOffset += 1) {
    history.push(createMockHistoryRow(addMonths(currentBillMonthStart, -monthOffset)));
  }

  return { currentBill, history };
};

let mockBillingDataset = null;

const getMockBillingDataset = () => {
  const expectedCurrentBillMonth = formatBillMonth(startOfMonth(addMonths(new Date(), -1)));
  if (!mockBillingDataset || mockBillingDataset.currentBill.billMonth !== expectedCurrentBillMonth) {
    mockBillingDataset = createMockBillingDataset(new Date(), 11);
  }
  return mockBillingDataset;
};

const runMockLookup = async ({ accountNumber, phoneNumber }) => {
  await wait(400);
  if (accountNumber && accountNumber !== mockAccount.accountNumber) {
    return { success: false, error: 'Account not found' };
  }
  if (phoneNumber && phoneNumber !== mockAccount.phoneNumber) {
    return { success: false, error: 'No account matches that phone' };
  }
  return { success: true, account: mockAccount };
};

const runMockCurrentBill = async (accountNumber) => {
  await wait(300);
  if (accountNumber !== mockAccount.accountNumber) {
    return { success: false, error: 'Bill not found' };
  }
  return { success: true, bill: getMockBillingDataset().currentBill };
};

const runMockBillingHistory = async (accountNumber) => {
  await wait(300);
  if (accountNumber !== mockAccount.accountNumber) {
    return { success: false, error: 'Billing history not found', bills: [] };
  }
  return { success: true, bills: getMockBillingDataset().history };
};

const runMockPayment = async ({ accountNumber, amount, method, cardLast4 }) => {
  await wait(800);
  if (amount <= 0) return { success: false, error: 'Invalid amount' };
  const { currentBill } = getMockBillingDataset();
  if (Number(amount).toFixed(2) !== currentBill.totals.amountDue.toFixed(2)) {
    return { success: false, error: 'Must pay full balance' };
  }

  return {
    success: true,
    payment: {
      paymentId: 'PAY-' + Math.random().toString(36).slice(2, 10).toUpperCase(),
      accountNumber,
      amount: Number(amount),
      method,
      cardLast4,
      processedAt: new Date().toISOString(),
      confirmationCode: 'CNF' + Date.now().toString().slice(-6)
    }
  };
};

// Utility to simulate latency
const wait = (ms = 500) => new Promise(r => setTimeout(r, ms));

const extractErrorMessage = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  if (typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
  if (typeof payload.title === 'string' && payload.title.trim()) return payload.title.trim();

  return null;
};

const parseBillingResponse = async (res) => {
  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  const isJsonResponse = contentType.includes('application/json');
  let payload = null;

  if (isJsonResponse) {
    try {
      payload = await res.json();
    } catch {
      throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
    }
  }

  if (!res.ok) {
    throw new Error(extractErrorMessage(payload) || BILLING_API_UNAVAILABLE_MESSAGE);
  }

  if (!isJsonResponse) {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }

  return payload;
};

// ----- API Functions -----
export async function lookupAccount({ accountNumber, phoneNumber }) {
  if (USE_MOCK) {
    logBillingDebug('Using mock billing service for lookupAccount.');
    return runMockLookup({ accountNumber, phoneNumber });
  }
  const params = new URLSearchParams();
  if (accountNumber) params.append('accountNumber', accountNumber);
  if (phoneNumber) params.append('phoneNumber', phoneNumber);

  logBillingDebug('Calling billing API for lookupAccount.', { url: BILLING_ROUTES.lookupAccount(params) });
  let res;
  try {
    res = await fetch(BILLING_ROUTES.lookupAccount(params));
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export async function getCurrentBill(accountNumber) {
  if (USE_MOCK) {
    logBillingDebug('Using mock billing service for getCurrentBill.');
    return runMockCurrentBill(accountNumber);
  }

  logBillingDebug('Calling billing API for getCurrentBill.', { url: BILLING_ROUTES.currentBill(accountNumber) });
  let res;
  try {
    res = await fetch(BILLING_ROUTES.currentBill(accountNumber));
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export async function getBillingHistory(accountNumber) {
  if (USE_MOCK) {
    logBillingDebug('Using mock billing service for getBillingHistory.');
    return runMockBillingHistory(accountNumber);
  }

  logBillingDebug('Calling billing API for getBillingHistory.', { url: BILLING_ROUTES.billingHistory(accountNumber) });
  let res;
  try {
    res = await fetch(BILLING_ROUTES.billingHistory(accountNumber));
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export async function submitPayment({ accountNumber, amount, method, cardLast4 }) {
  if (USE_MOCK) {
    logBillingDebug('Using mock billing service for submitPayment.');
    return runMockPayment({ accountNumber, amount, method, cardLast4 });
  }

  logBillingDebug('Calling billing API for submitPayment.', { url: BILLING_ROUTES.payments() });
  let res;
  try {
    res = await fetch(BILLING_ROUTES.payments(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountNumber, amount, method, cardLast4 })
    });
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export async function createServiceTransfer(data) {
  if (USE_MOCK) {
    await wait(500);
    const transferId = `TRF-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
    return {
      success: true,
      transfer: {
        transferId,
        accountNumber: data.accountNumber,
        customerName: data.customerName || 'Mock Customer',
        status: 'Pending',
        currentAddress: data.currentAddress,
        newAddress: data.disconnectOnly ? null : {
          street: data.newStreet,
          city: data.newCity,
          state: data.newState,
          postalCode: data.newPostalCode
        },
        moveOutDate: data.moveOutDate,
        moveInDate: data.moveInDate,
        disconnectOnly: data.disconnectOnly,
        notes: data.notes,
        createdAt: new Date().toISOString(),
        confirmedAt: null
      }
    };
  }

  let res;
  try {
    res = await fetch(`${getBillingApiBaseUrl()}/transfers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export async function confirmServiceTransfer(transferId) {
  if (USE_MOCK) {
    await wait(600);
    return {
      success: true,
      transfer: { transferId, status: 'Confirmed', confirmedAt: new Date().toISOString() }
    };
  }

  let res;
  try {
    res = await fetch(`${getBillingApiBaseUrl()}/transfers/${encodeURIComponent(transferId)}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  } catch {
    throw new Error(BILLING_API_UNAVAILABLE_MESSAGE);
  }
  return parseBillingResponse(res);
}

export function isMockBilling() { return USE_MOCK; }
